import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { Button, buttonVariants } from "@/components/ui/button.tsx";
import ErrorPage from "@/pages/ErrorPage.tsx";
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer.tsx";
import { useToast } from "@/components/ui/use-toast.ts";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import {
    getMentorById,
    getMentorMenteesWithSessions,
    type MenteeWithSessionStatuses,
    type MentorSessionForList,
} from "@/services/mentorService.ts";
import { unassignMentor } from "@/services/menteeService.ts";
import { getImgSrc } from "@/utils/file-upload.ts";
import { getDate, formatSessionTime12h, timezoneResolver } from "@/utils/time-helper.ts";
import { useTranslation } from "react-i18next";
import MentorInfoCard from "@/components/MentorInfoCard.tsx";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart.tsx";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog.tsx";

import { Mentor } from "@/types/Mentor.ts";
import { UserRole } from "@/types/UserRole.ts";
import { useAuth } from "@/contexts/AuthContext.tsx";
import { WhatsAppIcon } from "@/components/Icons.tsx";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { User, Trash2, Mail, Video, Star, HelpCircle } from "lucide-react";
import { getLocalizedFieldName } from "@/utils/translation-helper.ts";
import { MenteeWithPreferenceAndSuggestionsScore } from "@/types/Mentee.ts";
import { cn } from "@/lib/utils.ts";

const mentorIdFromParams = (id: string | undefined) => (id ? parseInt(id, 10) : -1);

/** Format: "24 Apr, 2025" */
function sessionDateLabel(sessionDate: string) {
    const d = new Date(sessionDate + "T12:00:00");
    const day = d.getDate();
    const month = d.toLocaleDateString("en-US", { month: "short" });
    const year = d.getFullYear();
    return `${day} ${month}, ${year}`;
}

/** True if session date is in the past (attendance is relevant). */
function isSessionPast(sessionDate: string) {
    const today = new Date().toISOString().slice(0, 10);
    return sessionDate < today;
}

function StarRating({ value }: { value: number }) {
    const rating = Math.max(0, Math.min(5, value));

    return (
        <div className="flex items-center gap-1" aria-label={`${rating.toFixed(1)} out of 5`}>
            {Array.from({ length: 5 }, (_, i) => {
                const fillPct = Math.max(0, Math.min(1, rating - i)) * 100;

                return (
                    <span key={i} className="relative inline-flex h-5 w-5">
                        <Star className="h-5 w-5 text-border" />
                        {fillPct > 0 && (
                            <span
                                className="absolute left-0 top-0 overflow-hidden"
                                style={{ width: `${fillPct}%` }}
                                aria-hidden="true"
                            >
                                <Star className="h-5 w-5 text-text-1" fill="currentColor" />
                            </span>
                        )}
                    </span>
                );
            })}
        </div>
    );
}

type MenteeToUnassign = { id: number; name: string };

const MentorMenteesPage = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const mentorId = mentorIdFromParams(id);
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { loggedInUser } = useAuth();
    const isAdmin = loggedInUser?.role === UserRole.ADMIN;
    const isGroupOwnerMentor = loggedInUser?.mentor?.id != null && Number(loggedInUser.mentor.id) === mentorId;
    const [menteeToUnassign, setMenteeToUnassign] = useState<MenteeToUnassign | null>(null);

    const { data: mentor, isFetching: mentorLoading } = useQuery({
        queryKey: [...queryKeys.fetchMentors.one, mentorId],
        queryFn: () => getMentorById(mentorId),
        enabled: mentorId > 0,
    });

    const { data: sessionsData, isFetching: sessionsLoading, isError: sessionsError, error: sessionsErrorData } = useQuery({
        queryKey: queryKeys.fetchMentors.menteesWithSessions(mentorId),
        queryFn: () => getMentorMenteesWithSessions(mentorId),
        enabled: mentorId > 0,
        retry: false,
    });

    const unassignMutation = useMutation({
        mutationFn: unassignMentor,
        onSuccess: async (mentee) => {
            await queryClient.setQueryData([...queryKeys.fetchMentors.one, mentorId], (m: Mentor | null) => {
                if (!m) return m;
                return {
                    ...m,
                    mentees: m.mentees ? m.mentees.filter((m) => m.id !== mentee.id) : [],
                };
            });
            await queryClient.invalidateQueries({ queryKey: queryKeys.fetchMentors.menteesWithSessions(mentorId) });
        },
    });

    const onUnassignRequest = useCallback((menteeId: number, menteeName: string) => {
        setMenteeToUnassign({ id: menteeId, name: menteeName });
    }, []);

    const onUnassignConfirm = useCallback(() => {
        if (!menteeToUnassign) return;
        unassignMutation.mutate(menteeToUnassign.id, {
            onSuccess: () => {
                setMenteeToUnassign(null);
                toast({
                    variant: "success",
                    title: t("common.successTitle"),
                    description: t("dashboard.menteeRemovedFromMembershipGroup"),
                });
            },
            onError: () => {
                toast({
                    variant: "destructive",
                    title: t("common.errorTitle"),
                    description: t("common.errorDesc"),
                });
            },
        });
    }, [menteeToUnassign, toast, t, unassignMutation]);

    const getFullMentee = (menteeId: number): MenteeWithPreferenceAndSuggestionsScore | undefined =>
        mentor?.mentees?.find((m) => m.id === menteeId);

    const isFetching = mentorLoading || sessionsLoading;
    const sessions: MentorSessionForList[] = sessionsData?.sessions ?? [];
    const mentees: MenteeWithSessionStatuses[] = sessionsData?.mentees ?? [];
    const canSendEmail = isAdmin || isGroupOwnerMentor;
    const canViewEngagementScore = isAdmin || isGroupOwnerMentor;
    const menteeEmails = mentees.map((m) => m.user.email).filter(Boolean) as string[];
    
    // Check for authorization errors
    const isUnauthorized = sessionsError && (sessionsErrorData as any)?.response?.status === 403;
    
    // Past sessions (for section visibility)
    const pastSessions = useMemo(() => {
        return sessions.filter((s) => isSessionPast(s.sessionDate));
    }, [sessions]);

    // Calculate attendees per session for the chart
    const chartData = useMemo(() => {
        if (sessions.length === 0 || mentees.length === 0) return [];
        
        return sessions.map((session, idx) => {
            const attendeesCount = mentees.filter((mentee) => {
                const sessionStatus = mentee.sessionStatuses.find((s) => s.sessionId === session.sessionId);
                return sessionStatus?.status === "attended";
            }).length;
            
            const totalMentees = mentees.length;
            const attendanceRate = totalMentees > 0 ? (attendeesCount / totalMentees) * 100 : 0;
            
            return {
                session: `Session ${idx + 1}`,
                attendees: attendeesCount,
                total: totalMentees,
                attendanceRate: Math.round(attendanceRate),
            };
        });
    }, [sessions, mentees]);

    const engagementScore = useMemo(() => {
        if (pastSessions.length === 0 || mentees.length === 0) return null;

        const pastSessionIds = pastSessions.map((s) => s.sessionId);
        const totalPossibleAttendance = mentees.length * pastSessionIds.length;

        let attendedCount = 0;
        let feedbackCount = 0;
        mentees.forEach((mentee) => {
            pastSessionIds.forEach((sessionId) => {
                const st = mentee.sessionStatuses.find((x) => x.sessionId === sessionId);
                if (st?.status === "attended") {
                    attendedCount += 1;
                }
                if (st?.feedbackSubmitted) {
                    feedbackCount += 1;
                }
            });
        });

        const attendancePct =
            totalPossibleAttendance > 0 ? attendedCount / totalPossibleAttendance : 0; // 0..1
        const feedbackPct =
            totalPossibleAttendance > 0 ? feedbackCount / totalPossibleAttendance : 0; // 0..1 (share of mentees who left feedback)
        const notesPct =
            pastSessions.filter((s) => s.hasMentorNote).length / pastSessions.length; // 0..1

        const weighted = attendancePct * 0.6 + feedbackPct * 0.2 + notesPct * 0.2; // 0..1
        const scoreOutOfFive = Math.round(weighted * 5 * 10) / 10; // 1dp

        return {
            scoreOutOfFive,
            attendancePct,
            feedbackPct,
            notesPct,
            pastSessionsCount: pastSessions.length,
        };
    }, [mentees, pastSessions]);

    const openMailToMentees = () => {
        if (menteeEmails.length === 0) return;
        const to = menteeEmails.join(",");
        window.location.href = `mailto:${to}`;
    };

    const mentorTitle = mentor ? (mentor[getLocalizedFieldName("title") as keyof Mentor] as string) : "";
    const mentorUniversity = mentor ? (mentor[getLocalizedFieldName("university") as keyof Mentor] as string) : "";
    const mentorCareerFocus = mentor?.careerFocus ? (mentor.careerFocus[getLocalizedFieldName("name") as keyof typeof mentor.careerFocus] as string) : "";
    const mentorFullName =
        mentor?.user?.fullName ??
        (mentor?.user?.firstName && mentor?.user?.lastName
            ? `${mentor.user.firstName} ${mentor.user.lastName}`.trim()
            : mentor?.user?.firstName ?? mentor?.user?.lastName ?? t("common.mentor"));
    const mentorProfileSrc = getImgSrc(mentor?.user?.profilePictureSrc ?? undefined);

    // Show full-page 403 error if unauthorized
    if (isUnauthorized) {
        return (
            <ErrorPage 
                httpCode="403" 
                errorTitle="unauthorized" 
                errorMessage="unauthorizedDesc"
            />
        );
    }

    return (
        <>
            <div className="container pb-12 pt-8">
                {mentor && (
                    <MentorInfoCard
                        mentorId={mentor.id}
                        fullName={mentorFullName}
                        profileSrc={mentorProfileSrc}
                        title={mentorTitle}
                        university={mentorUniversity}
                        careerFocus={mentorCareerFocus}
                    />
                )}

                <header className="mb-8 mt-8 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
                    <div>
                        <h1 className="flex items-center gap-2 text-3xl font-bold text-text-1">
                            <span>{t("mentor.mentorshipGroupPageTitle")}</span>
                            {mentor?.isOnWhatsApp && (
                                <WhatsAppIcon className="h-7 w-7 text-[#25D366]" title={t("mentor.availableOnWhatsApp")} />
                            )}
                        </h1>
                        <p className="mt-1 text-base text-text-2">
                            {t("mentor.mentorshipGroupSubtitle")}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                        {mentor?.personalMeetingLink?.trim() ? (
                            <Button variant="outline" size="sm" className="gap-2" asChild>
                                <a
                                    href={mentor.personalMeetingLink.trim()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Video className="h-4 w-4" />
                                    {t("dashboard.joinMeeting")}
                                </a>
                            </Button>
                        ) : (
                            <Button variant="outline" size="sm" className="gap-2" disabled>
                                <Video className="h-4 w-4" />
                                {t("dashboard.joinMeeting")}
                            </Button>
                        )}
                        {canSendEmail && menteeEmails.length > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={openMailToMentees}
                                className="gap-2"
                            >
                                <Mail className="h-4 w-4" />
                                {t("mentor.sendEmailToMentees")}
                            </Button>
                        )}
                    </div>
                </header>

                {isFetching && (
                    <div className="flex flex-col gap-4">
                        <Skeleton className="h-20 w-full rounded-xl" />
                        <Skeleton className="h-20 w-full rounded-xl" />
                        <Skeleton className="h-20 w-full rounded-xl" />
                    </div>
                )}

                {!isFetching && mentees.length === 0 && (
                    <div className="rounded-xl border border-border bg-bg-card px-6 py-12 text-center shadow-sm">
                        <p className="text-text-2">{t("mentor.noMentees")}</p>
                    </div>
                )}

                {!isFetching && mentees.length > 0 && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]">
                            <div className="space-y-6">
                                {sessions.length > 0 && (
                                    <section className="rounded-xl border border-border bg-bg-card p-4 shadow-sm sm:p-6">
                                        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h2 className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                                        {t("mentor.sessions")}
                                                    </h2>
                                                    {mentor?.timezone && (
                                                        <span className="text-xs text-text-2">
                                                            {t("mentor.sessionsTimezoneNotice", {
                                                                timezone: timezoneResolver[mentor.timezone]?.[i18n.language === "fr" ? "fr" : "en"] ?? mentor.timezone,
                                                            })}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {sessions.map((s, idx) => {
                                                        const past = isSessionPast(s.sessionDate);
                                                        const pillContent = (
                                                            <>
                                                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-xs font-medium text-text-2">
                                                                    {idx + 1}
                                                                </span>
                                                                <span className="text-sm text-text-1">{sessionDateLabel(s.sessionDate)}</span>
                                                                {s.sessionTime && (
                                                                    <span className="text-xs text-text-3">{formatSessionTime12h(s.sessionTime)}</span>
                                                                )}
                                                            </>
                                                        );
                                                        const pillClass = "inline-flex items-center gap-2 rounded-full border border-border bg-bg-card px-3 py-1.5 text-sm shadow-sm";
                                                        return past ? (
                                                            <Link
                                                                key={s.sessionId}
                                                                to={`/session-attendance?sessionId=${s.sessionId}`}
                                                                className={cn(pillClass, "hover:border-accent/50 hover:bg-bg-elevated/80 transition-colors")}
                                                                title={getDate(new Date(s.sessionDate + "T12:00:00")) + (s.sessionTime ? ` · ${formatSessionTime12h(s.sessionTime)}` : "") + " — " + t("sessionAttendance.markAttendance")}
                                                            >
                                                                {pillContent}
                                                            </Link>
                                                        ) : (
                                                            <span
                                                                key={s.sessionId}
                                                                className={cn(pillClass, "opacity-90")}
                                                                title={getDate(new Date(s.sessionDate + "T12:00:00")) + (s.sessionTime ? ` · ${formatSessionTime12h(s.sessionTime)}` : "")}
                                                            >
                                                                {pillContent}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-4 text-xs text-text-2">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="h-3 w-3 rounded-full bg-status-success" />
                                                    {t("mentee.sessionsAttended")}
                                                </span>
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="h-3 w-3 rounded-full bg-status-danger" />
                                                    {t("mentee.sessionsMissed")}
                                                </span>
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="h-3 w-3 rounded-full border-2 border-border bg-text-3/20" />
                                                    {t("mentee.sessionsUpcoming")}
                                                </span>
                                            </div>
                                        </div>
                                    </section>
                                )}

                                <section>
                            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-2">
                                {t("navbar.mentees")}
                            </h2>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-4xl">
                                {mentees.map((row) => {
                                    const fullMentee = getFullMentee(row.id);
                                    return (
                                        <Card
                                            key={row.id}
                                            className="overflow-hidden rounded-xl border border-border bg-bg-card shadow-sm transition-shadow hover:shadow-md"
                                        >
                                            <CardContent className="p-0">
                                                <div className="flex flex-col gap-4 p-4 sm:p-6">
                                                    <div className="flex min-w-0 items-start gap-4">
                                                        <Avatar className="h-12 w-12 shrink-0 sm:h-14 sm:w-14">
                                                            <AvatarImage
                                                                src={getImgSrc(row.user.profilePictureSrc ?? undefined)}
                                                                alt={row.user.fullName}
                                                            />
                                                            <AvatarFallback className="bg-accent/15 text-text-1 text-base font-semibold sm:text-lg">
                                                                {row.user.fullName?.charAt(0) ?? "M"}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="truncate font-semibold text-text-1">{row.user.fullName}</p>
                                                            <p className="truncate text-sm text-text-2" title={row.user.email}>
                                                                {row.user.email}
                                                            </p>
                                                        </div>
                                                        <div className="flex shrink-0 items-center gap-1">
                                                            {isAdmin && mentor?.id != null && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-9 w-9 rounded-lg text-text-2 hover:bg-status-danger/10 hover:text-status-danger"
                                                                    title={t("common.remove")}
                                                                    onClick={() => onUnassignRequest(row.id, row.user.fullName ?? "")}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                            {fullMentee ? (
                                                                <Drawer>
                                                                    <DrawerTrigger asChild>
                                                                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" title={t("common.details")}>
                                                                            <User className="h-4 w-4" />
                                                                        </Button>
                                                                    </DrawerTrigger>
                                                                    <DrawerContent>
                                                                        <MenteeDetailsDrawer
                                                                            mentee={fullMentee}
                                                                            onUnassign={isAdmin && mentor?.id != null ? () => onUnassignRequest(row.id, row.user.fullName ?? "") : undefined}
                                                                        />
                                                                    </DrawerContent>
                                                                </Drawer>
                                                            ) : (
                                                                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" asChild title={t("common.details")}>
                                                                    <Link to={`/mentees/${row.id}`}>
                                                                        <User className="h-4 w-4" />
                                                                    </Link>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {sessions.length > 0 && (
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            {sessions.map((s, idx) => {
                                                                const st = row.sessionStatuses.find((x) => x.sessionId === s.sessionId);
                                                                const status = st?.status ?? "upcoming";
                                                                const dateLabel = sessionDateLabel(s.sessionDate) + (s.sessionTime ? ` · ${formatSessionTime12h(s.sessionTime)}` : "");
                                                                const circleClass = cn(
                                                                    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-opacity",
                                                                    status === "attended" && "bg-status-success/15 text-status-success",
                                                                    status === "missed" && "bg-status-danger/15 text-status-danger",
                                                                    status === "upcoming" && "border border-border bg-text-3/15 text-text-2"
                                                                );
                                                                const title = `${idx + 1}. ${dateLabel}` + (status !== "upcoming" ? ` — ${t("sessionAttendance.markAttendance")}` : "");
                                                                return status === "upcoming" ? (
                                                                    <span key={s.sessionId} title={title} className={circleClass}>
                                                                        {idx + 1}
                                                                    </span>
                                                                ) : (
                                                                    <Link
                                                                        key={s.sessionId}
                                                                        to={`/session-attendance?sessionId=${s.sessionId}`}
                                                                        title={title}
                                                                        className={cn(circleClass, "hover:opacity-90")}
                                                                    >
                                                                        {idx + 1}
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </section>
                            </div>
                            
                            {/* Engagement Score & Attendance Chart */}
                            {(pastSessions.length > 0 && mentees.length > 0) || chartData.length > 0 ? (
                                <section className="rounded-xl border border-border bg-bg-card p-4 shadow-sm sm:p-6">
                                    {canViewEngagementScore && engagementScore && (
                                        <div className="mb-6">
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-baseline gap-2">
                                                        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                                            {t("mentor.engagementScore")}
                                                        </h2>
                                                    </div>
                                                    <p className="mt-1 text-xs text-text-3">
                                                        {t("mentor.pastSessionsCount", { count: engagementScore.pastSessionsCount })}
                                                    </p>
                                                </div>

                                                <div className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <StarRating value={engagementScore.scoreOutOfFive} />
                                                        <Dialog>
                                                            <DialogTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-2 hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card"
                                                                    aria-label={t("mentor.engagementScoreInfo")}
                                                                    title={t("mentor.engagementScoreInfo")}
                                                                >
                                                                    <HelpCircle className="h-4 w-4" />
                                                                </button>
                                                            </DialogTrigger>
                                                            <DialogContent>
                                                                <DialogHeader>
                                                                    <DialogTitle>{t("mentor.engagementScore")}</DialogTitle>
                                                                    <DialogDescription>
                                                                        {t("mentor.engagementScoreDescription")}
                                                                    </DialogDescription>
                                                                </DialogHeader>
                                                                <div className="space-y-4 text-sm text-text-2">
                                                                    <div className="space-y-2">
                                                                        <div className="font-semibold text-text-1">
                                                                            {t("mentor.howScoreCalculated")}
                                                                        </div>
                                                                        <ul className="list-disc pl-5 space-y-1">
                                                                            <li>
                                                                                <span className="font-medium text-text-1">{t("mentor.attendanceWeight")}</span>{" "}
                                                                                — {t("mentor.attendanceExplanation")}
                                                                            </li>
                                                                            <li>
                                                                                <span className="font-medium text-text-1">{t("mentor.feedbackWeight")}</span>{" "}
                                                                                — {t("mentor.feedbackExplanation")}
                                                                            </li>
                                                                            <li>
                                                                                <span className="font-medium text-text-1">{t("mentor.notesWeight")}</span>{" "}
                                                                                — {t("mentor.notesExplanation")}
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        <div className="font-semibold text-text-1">
                                                                            {t("mentor.tipsToIncrease")}
                                                                        </div>
                                                                        <ul className="list-disc pl-5 space-y-1">
                                                                            <li>{t("mentor.tipAttendance")}</li>
                                                                            <li>{t("mentor.tipFeedback")}</li>
                                                                            <li>{t("mentor.tipNotes")}</li>
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </DialogContent>
                                                        </Dialog>
                                                    </div>
                                                    <div className="mt-1 text-xs text-text-2">
                                                        {engagementScore.scoreOutOfFive.toFixed(1)} {t("mentor.outOfFiveStars")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Attendance Chart */}
                                    {chartData.length > 0 && (
                                        <>
                                            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-2 mb-4">
                                                {t("mentor.attendanceChart")}
                                            </h2>
                                    <div className="[&_.recharts-legend-item-text]:!text-text-1">
                                    <ChartContainer 
                                        className="h-[300px] w-full" 
                                        config={{
                                            attendees: { label: t("mentor.attendees"), color: "rgb(var(--accent) / 1)" },
                                            attendanceRate: { label: t("mentor.attendanceRate"), color: "#2BE081" },
                                        }}
                                    >
                                        <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                                            <XAxis 
                                                dataKey="session" 
                                                tickLine={false} 
                                                axisLine={false} 
                                                tick={{ fontSize: 11, fill: "rgb(var(--text-3) / 1)" }} 
                                            />
                                            <YAxis 
                                                yAxisId="left"
                                                tickLine={false} 
                                                axisLine={false} 
                                                tick={{ fontSize: 11, fill: "rgb(var(--text-3) / 1)" }} 
                                                width={28}
                                            />
                                            <YAxis 
                                                yAxisId="right"
                                                orientation="right"
                                                tickLine={false} 
                                                axisLine={false} 
                                                tick={{ fontSize: 11, fill: "rgb(var(--text-3) / 1)" }} 
                                                width={40}
                                                domain={[0, 100]}
                                            />
                                            <ChartTooltip content={<ChartTooltipContent />} />
                                            <Bar 
                                                yAxisId="left"
                                                dataKey="attendees" 
                                                name={t("mentor.attendees")}
                                                fill="var(--color-attendees)" 
                                                radius={[4, 4, 0, 0]}
                                            />
                                            <Line 
                                                yAxisId="right"
                                                type="monotone" 
                                                dataKey="attendanceRate" 
                                                name={t("mentor.attendanceRate")}
                                                stroke="var(--color-attendanceRate)" 
                                                strokeWidth={2}
                                                dot={false}
                                                activeDot={{ r: 6, fill: "#2BE081", strokeWidth: 2, stroke: "#2BE081" }}
                                            />
                                            <Legend 
                                                wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
                                                formatter={(value) => {
                                                    if (value === "attendees") return t("mentor.attendees");
                                                    if (value === "attendanceRate") return t("mentor.attendanceRate");
                                                    return value;
                                                }}
                                                style={{ color: "rgb(var(--text-1) / 1)" }}
                                            />
                                        </ComposedChart>
                                    </ChartContainer>
                                    </div>
                                        </>
                                    )}
                                </section>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
            <AlertDialog open={!!menteeToUnassign} onOpenChange={(open) => !open && setMenteeToUnassign(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("common.areYouSure")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {menteeToUnassign && (
                                <>
                                    {t("dashboard.removeMenteeFromGroupConfirmDesc")}
                                    {menteeToUnassign.name && (
                                        <span className="mt-2 block font-medium">
                                            {menteeToUnassign.name}
                                        </span>
                                    )}
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={unassignMutation.isPending}>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                onUnassignConfirm();
                            }}
                            disabled={unassignMutation.isPending}
                            className="bg-status-danger text-white hover:opacity-95"
                        >
                            {t("common.remove")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

function MenteeDetailsDrawer({
    mentee,
    onUnassign,
}: {
    mentee: MenteeWithPreferenceAndSuggestionsScore;
    onUnassign?: () => void;
}) {
    const { t } = useTranslation();
    const getLocalizedValue = (key: string) =>
        mentee[getLocalizedFieldName(key) as keyof MenteeWithPreferenceAndSuggestionsScore] as string;

    return (
        <div className="mx-auto w-full max-w-lg">
            <DrawerHeader className="px-2">
                <DrawerTitle>{mentee.user.fullName}</DrawerTitle>
                <DrawerDescription>{mentee.user.email}</DrawerDescription>
            </DrawerHeader>
            <div className="grid px-2">
                <div className="grid grid-cols-5">
                    <div className="col-span-2 p-2 font-semibold">{t("criteria.discipline")}</div>
                    <div className="col-span-3 p-2">{getLocalizedValue("discipline")}</div>
                </div>
                <div className="grid grid-cols-5">
                    <div className="col-span-2 p-2 font-semibold">{t("mentee.supervisor")}</div>
                    <div className="col-span-3 p-2">{mentee.supervisor}</div>
                </div>
                <div className="grid grid-cols-5">
                    <div className="col-span-2 p-2 font-semibold">{t("mentee.institution")}</div>
                    <div className="col-span-3 p-2">{getLocalizedValue("institution")}</div>
                </div>
                <div className="grid grid-cols-5">
                    <div className="col-span-2 p-2 font-semibold">{t("mentee.studentStatus")}</div>
                    <div className="col-span-3 p-2">{getLocalizedValue("studentStatus")}</div>
                </div>
                {mentee.phoneNumber && (
                    <div className="grid grid-cols-5">
                        <div className="col-span-2 p-2 font-semibold">{t("common.phoneNumber")}</div>
                        <div className="col-span-3 p-2">{mentee.phoneNumber}</div>
                    </div>
                )}
            </div>
            <DrawerFooter className="px-2">
                <Link to={`/mentees/${mentee.id}`} className={buttonVariants({ variant: "default" })}>
                    {t("profile.fullProfile")}
                </Link>
                {onUnassign && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onUnassign}
                        className={buttonVariants({ variant: "outline" })}
                    >
                        {t("common.remove")}
                    </Button>
                )}
                <DrawerClose asChild>
                    <Button variant="outline">{t("common.goBack")}</Button>
                </DrawerClose>
            </DrawerFooter>
        </div>
    );
}

export default MentorMenteesPage;
